/**
 * Submission Pack Store (Specs/submission-pack-spec.md)
 *
 * One read per bid — `GET /bids/{id}/submission-pack` — powers the whole Pack
 * page: the grouped list, the roll-up, the blockers strip and the
 * "Needs from you" count.
 *
 * Writes have no pack-specific route. Every mutation rides the extraction
 * endpoints the Analysis lens already uses, then silently re-reads the pack —
 * readiness is derived server-side, so a local patch would show a stale
 * verdict. The pack is a READ MODEL, never a third parallel store of
 * checklist rows.
 */

import { create } from 'zustand'
import {
  api,
  createPackAsk,
  createPackItem,
  deletePackItem,
  exportSubmissionPack,
  getSubmissionPack,
  requestUploadUrl,
  updatePackAsk,
  updatePackItem,
  uploadFileToS3,
  type BidTodo,
  type CreatePackItemInput,
  type ExportOptions,
  type PackAsk,
  type PackAskPatch,
  type PackItemPatch,
  type PackPendingAiChange,
  type SubmissionPackResponse,
  type SubmissionStatus,
} from '@/lib/api'

/** Link target for one checklist item — an artifact section OR a document, never both. */
export type PackLinkTarget =
  | { kind: 'artifact'; artifactId: string; sectionKey: string | null }
  | { kind: 'document'; documentId: string }

interface SubmissionPackState {
  /** Bid the current state belongs to (stores are global). */
  bidId: string | null
  pack: SubmissionPackResponse | null
  isLoading: boolean
  /** True from the export POST until the download fires (or the run fails). */
  isExporting: boolean
  error: string | null

  load: (bidId: string) => Promise<void>
  /**
   * Export the pack as a zip. Resolves with the manifest's missing count once
   * the browser download has been triggered; rejects with the worker's error so
   * the caller can surface it. Lives in the store only so the header button can
   * show progress while the dialog that started the run is closed.
   */
  /** Resolves with the manifest's missing count and the documents that carry last accepted text (R0-A). */
  /** `options.acknowledgeTemplateLoss` resends after the template warning (R3 D4). */
  exportPack: (templateId?: string, options?: ExportOptions) => Promise<{ missingCount: number; pendingAiChanges: PackPendingAiChange[] }>
  /** Silent re-fetch after any mutation — no spinner, no error banner. */
  refresh: () => Promise<void>
  reset: () => void

  /** Link an item to an artifact section or a document. */
  linkItem: (itemId: string, target: PackLinkTarget) => Promise<void>
  /** Clear both link columns. */
  unlinkItem: (itemId: string) => Promise<void>
  /**
   * Upload a file as a bid document, then link it to the item.
   *
   * Reuses the existing two-step document flow verbatim — presign, S3 PUT,
   * register — so the pack never grows its own upload machinery (spec P1.3).
   */
  uploadAndLinkItem: (itemId: string, file: File) => Promise<void>
  /** A status sets `status_source='manual'`; null resets to the derived verdict. */
  setStatus: (itemId: string, status: SubmissionStatus | null) => Promise<void>
  setAcknowledged: (itemId: string, acknowledge: boolean) => Promise<void>
  setOwner: (itemId: string, ownerId: string | null) => Promise<void>
  /** Add a manual pack row. Returns its id so the caller can open the drawer on it. */
  addItem: (input: CreatePackItemInput) => Promise<string | null>
  /**
   * Hard-delete a row. Only manual rows are ever deleted from the Pack —
   * extracted rows are dismissed on Analysis (drawer-PR-1 convention).
   */
  removeItem: (itemId: string) => Promise<void>

  // --- "Needs from you" asks (spec P3) ---

  /**
   * The ask id for a `[TODO]` row, creating its overlay when there isn't one.
   *
   * A marker has no id of its own, so the answer/owner/resolve state needs a
   * row to live on. The POST is idempotent on the marker's key, so this is safe
   * to call twice — it returns the SERVER's id, which may be an existing
   * overlay rather than a new row.
   */
  ensureOverlay: (todo: BidTodo, overlay: PackAsk | null) => Promise<string | null>
  answerAsk: (askId: string, answer: string) => Promise<void>
  resolveAsk: (askId: string) => Promise<void>
  setAskOwner: (askId: string, ownerId: string | null) => Promise<void>
  /** Add a manual ask. Returns its id. */
  addAsk: (text: string, ownerId?: string | null) => Promise<string | null>
}

export const selectNeedsFromYouCount = (s: SubmissionPackState): number =>
  s.pack?.needsFromYouCount ?? 0

export const useSubmissionPackStore = create<SubmissionPackState>((set, get) => ({
  bidId: null,
  pack: null,
  isLoading: false,
  isExporting: false,
  error: null,

  exportPack: async (templateId, options) => {
    const bidId = get().bidId
    // Rejects rather than resolving with nothing: a silent result would toast a successful
    // export that never ran.
    if (!bidId) throw new Error('No bid is loaded')
    set({ isExporting: true })
    try {
      return await exportSubmissionPack(bidId, templateId, options)
    } finally {
      set({ isExporting: false })
    }
  },

  load: async (bidId) => {
    if (get().bidId !== bidId) set({ bidId, pack: null, error: null })
    set({ isLoading: true, error: null })
    try {
      const pack = await getSubmissionPack(bidId)
      // Bid may have changed while in flight
      if (get().bidId === bidId) set({ pack, isLoading: false })
    } catch (error) {
      if (get().bidId === bidId) {
        set({
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to load the submission pack',
        })
      }
    }
  },

  refresh: async () => {
    const bidId = get().bidId
    if (!bidId) return
    try {
      const pack = await getSubmissionPack(bidId)
      if (get().bidId === bidId) set({ pack })
    } catch {
      // Silent: the visible state is the last good pack, and the mutation that
      // triggered this refresh already reported its own failure.
    }
  },

  linkItem: async (itemId, target) => {
    const patch: PackItemPatch =
      target.kind === 'artifact'
        ? {
            linkedArtifactId: target.artifactId,
            linkedSectionKey: target.sectionKey,
            linkedDocumentId: null,
          }
        : {
            linkedDocumentId: target.documentId,
            linkedArtifactId: null,
            linkedSectionKey: null,
          }
    await patchItem(get, itemId, patch)
  },

  unlinkItem: async (itemId) => {
    await patchItem(get, itemId, {
      linkedArtifactId: null,
      linkedSectionKey: null,
      linkedDocumentId: null,
    })
  },

  uploadAndLinkItem: async (itemId, file) => {
    const bidId = get().bidId
    if (!bidId) return
    const { documentId, uploadUrl, s3Key } = await requestUploadUrl(
      file.name,
      file.type,
      file.size,
      bidId
    )
    await uploadFileToS3(uploadUrl, file)
    await api.post('/documents', {
      documentId,
      name: file.name,
      // Submission attachments are the bidder's own material, not tender input.
      type: 'other',
      mimeType: file.type,
      size: file.size,
      s3Key,
      bidId,
    })
    await get().linkItem(itemId, { kind: 'document', documentId })
  },

  setStatus: async (itemId, status) => {
    await patchItem(get, itemId, status === null ? { statusSource: 'derived' } : { status })
  },

  setAcknowledged: async (itemId, acknowledge) => {
    await patchItem(get, itemId, { acknowledge })
  },

  setOwner: async (itemId, ownerId) => {
    await patchItem(get, itemId, { ownerId })
  },

  addItem: async (input) => {
    const bidId = get().bidId
    if (!bidId) return null
    const { item } = await createPackItem(bidId, input)
    await get().refresh()
    return item?.id ?? null
  },

  removeItem: async (itemId) => {
    const bidId = get().bidId
    if (!bidId) return
    await deletePackItem(bidId, itemId)
    await get().refresh()
  },

  ensureOverlay: async (todo, overlay) => {
    if (overlay) return overlay.id
    const bidId = get().bidId
    if (!bidId) return null
    const { ask } = await createPackAsk(bidId, {
      kind: 'todo',
      text: todo.text,
      artifactId: todo.artifactId,
      sectionId: todo.sectionId,
    })
    // No refresh: a bare overlay carries no answer, no owner and no resolution,
    // so it changes nothing the list renders. The PATCH that follows refreshes.
    return ask.id
  },

  answerAsk: async (askId, answer) => {
    await patchAsk(get, askId, { answer })
  },

  resolveAsk: async (askId) => {
    await patchAsk(get, askId, { resolved: true })
  },

  setAskOwner: async (askId, ownerId) => {
    await patchAsk(get, askId, { ownerId })
  },

  addAsk: async (text, ownerId = null) => {
    const bidId = get().bidId
    if (!bidId) return null
    const { ask } = await createPackAsk(bidId, { kind: 'manual', text, ownerId })
    await get().refresh()
    return ask.id
  },

  reset: () =>
    set({ bidId: null, pack: null, isLoading: false, isExporting: false, error: null }),
}))

/** Shared write path: PATCH the extraction row, then re-read the derived pack. */
async function patchItem(
  get: () => SubmissionPackState,
  itemId: string,
  patch: PackItemPatch
): Promise<void> {
  const bidId = get().bidId
  if (!bidId) return
  await updatePackItem(bidId, itemId, patch)
  await get().refresh()
}

/** Same shape for asks: PATCH the ask, then re-read the merged pack. */
async function patchAsk(
  get: () => SubmissionPackState,
  askId: string,
  patch: PackAskPatch
): Promise<void> {
  const bidId = get().bidId
  if (!bidId) return
  await updatePackAsk(bidId, askId, patch)
  await get().refresh()
}
